import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { computeCalc, usd, pct } from "@/lib/format";
import { cn } from "@/lib/utils";
import { VerdictBadge, verdictStyle } from "@/components/common/primitives";
import {
  Radar, ExternalLink, ChevronDown, X, Eye, Bookmark, ScanLine, LayoutDashboard,
  History as HistoryIcon, AlertTriangle, Package,
} from "lucide-react";
import { toast } from "sonner";

// Purpose-built MV3 side-panel surface (420px baseline). Not desktop JSX squeezed in.
const NAV = [
  { k: "analyze", icon: Package, label: "Analyze" },
  { k: "scan", icon: ScanLine, label: "Scan" },
  { k: "saved", icon: Bookmark, label: "Saved" },
  { k: "history", icon: HistoryIcon, label: "History" },
  { k: "dash", icon: LayoutDashboard, label: "Dash" },
];

function Accordion({ title, children, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-edge-subtle">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between py-2.5 text-compact font-medium text-ink-primary">
        {title}<ChevronDown className={cn("h-4 w-4 transition-transform", open && "rotate-180")} />
      </button>
      {open && <div className="pb-3 ez-fade-in">{children}</div>}
    </div>
  );
}

export default function SidePanel() {
  const [product, setProduct] = useState(null);
  const [input, setInput] = useState(null);
  const [tab, setTab] = useState("analyze");
  const [phase] = useState("found"); // idle|detecting|found|no_product

  useEffect(() => { api.product().then((r) => { setProduct(r.data.product); setInput(r.data.product.calc_input); }); }, []);

  const calc = useMemo(() => (input ? computeCalc(input) : null), [input]);
  const verdict = useMemo(() => {
    if (!calc || !input) return null;
    const roi = calc.roi_percent ?? 0;
    if (input.acquisition_cost > calc.maximum_purchase_cost) return "REJECT";
    if (roi < 30) return "REJECT";
    if (roi < 35) return "WATCH";
    if (roi >= 45 && input.confidence >= 80) return "STRONG BUY";
    return "TEST BUY";
  }, [calc, input]);

  const setF = (k, v) => setInput((p) => ({ ...p, [k]: parseFloat(v) || 0 }));

  return (
    <div className="min-h-screen bg-surface-1 flex justify-center">
      <div className="w-full max-w-[420px] bg-surface-1 min-h-screen flex flex-col relative shadow-menu">
        {/* header 52px */}
        <header className="sticky top-0 z-10 h-[52px] bg-navy-900 flex items-center justify-between px-3">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-ctrl bg-emerald-600 grid place-items-center"><Radar className="h-4 w-4 text-white" /></div>
            <div className="text-ink-inverse text-compact font-semibold leading-none">Ezisell<div className="text-micro text-ink-inverse/50 font-normal">Sam's Club detected</div></div>
          </div>
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-amber-600" title="fixture" />
            <a href="/app/analyzer" className="h-8 w-8 grid place-items-center rounded-ctrl bg-navy-800 text-ink-inverse"><ExternalLink className="h-4 w-4" /></a>
          </div>
        </header>

        {/* fixture strip */}
        <div className="bg-amber-100 text-amber-600 text-micro font-mono px-3 py-1 text-center">FIXTURE / DEMO — not live data</div>

        <div className="flex-1 overflow-y-auto ez-scroll px-3 pt-3 pb-40">
          {tab === "analyze" && product && calc && (
            <>
              {/* identity */}
              <div className="flex gap-2.5">
                <div className="h-14 w-14 rounded-ctrl bg-surface-2 grid place-items-center shrink-0"><Package className="h-6 w-6 text-ink-muted" /></div>
                <div className="min-w-0">
                  <div className="text-compact font-medium text-ink-primary line-clamp-2 leading-tight">{product.source.title}</div>
                  <div className="text-micro font-mono text-ink-muted mt-0.5">{usd(product.source.price)} · {product.match.confidence}% · {product.source.pack}</div>
                </div>
              </div>

              {/* verdict */}
              <div className={cn("mt-3 rounded-panel overflow-hidden flex", verdictStyle(verdict).bg)}>
                <div className={cn("w-1.5", verdictStyle(verdict).rail)} />
                <div className="p-2.5 flex-1">
                  <VerdictBadge verdict={verdict} />
                  <p className={cn("text-micro mt-1", verdictStyle(verdict).text)}>
                    {verdict === "REJECT" ? `Cost ${usd(input.acquisition_cost)} vs max ${usd(calc.maximum_purchase_cost)}` : `${pct(calc.roi_percent)} ROI · ${usd(calc.cost_gap)} under max cost`}
                  </p>
                </div>
              </div>

              {/* profit strip: 3-col continuous */}
              <div className="mt-3 rounded-panel border border-edge-subtle bg-surface-0 flex divide-x divide-edge-subtle">
                {[["Profit", usd(calc.net_profit)], ["ROI", pct(calc.roi_percent)], ["Max cost", usd(calc.maximum_purchase_cost)]].map(([l, v]) => (
                  <div key={l} className="flex-1 px-2 py-2 text-center"><div className="text-micro font-mono uppercase text-ink-muted">{l}</div><div className="font-mono tabnum text-compact font-semibold text-ink-primary">{v}</div></div>
                ))}
              </div>

              {/* editable */}
              <div className="mt-3 grid grid-cols-2 gap-2">
                <label className="block"><span className="text-micro font-mono uppercase text-ink-muted">Source cost</span>
                  <input type="number" step="0.01" value={input.acquisition_cost} onChange={(e) => setF("acquisition_cost", e.target.value)} className="mt-0.5 w-full h-8 px-2 rounded-ctrl border border-edge-subtle text-compact font-mono" /></label>
                <label className="block"><span className="text-micro font-mono uppercase text-ink-muted">Sale price</span>
                  <input type="number" step="0.01" value={input.sale_price} onChange={(e) => setF("sale_price", e.target.value)} className="mt-0.5 w-full h-8 px-2 rounded-ctrl border border-edge-subtle text-compact font-mono" /></label>
              </div>

              {/* top risk */}
              <div className="mt-3 flex items-start gap-2 rounded-ctrl bg-red-100/50 border border-red-600/20 p-2">
                <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5" />
                <div className="text-micro text-red-600">{product.risks[0].title}<div className="text-ink-secondary mt-0.5">{product.risks[0].evidence}</div></div>
              </div>

              {/* accordions */}
              <div className="mt-3">
                <Accordion title="Competition" defaultOpen>
                  <div className="text-micro font-mono space-y-1 text-ink-secondary">
                    <div className="flex justify-between"><span>Buy Box</span><span>{product.competition.buy_box_owner}</span></div>
                    <div className="flex justify-between"><span>Offers</span><span>{product.competition.total_offers} ({product.competition.wfs_offers} WFS)</span></div>
                  </div>
                </Accordion>
                <Accordion title="Demand"><div className="text-micro text-ink-secondary">Test qty {product.demand.suggested_test_qty} · {product.demand.qty_rationale}</div></Accordion>
                <Accordion title="History"><div className="text-micro text-ink-muted">Open full chart in dashboard — insufficient inline space.</div></Accordion>
                <Accordion title="Fees"><div className="text-micro font-mono text-ink-secondary">Referral 15% · WFS {usd(input.wfs_fee)} · landed {usd(calc.landed_cost)}</div></Accordion>
              </div>
            </>
          )}

          {tab === "scan" && (
            <div className="text-center py-16"><ScanLine className="h-10 w-10 mx-auto text-ink-muted mb-3" /><p className="text-compact text-ink-primary">Point camera at a barcode</p><p className="text-micro text-ink-muted mt-1">Manual UPC fallback · offline queue supported</p></div>
          )}
          {tab === "saved" && <div className="text-center py-16 text-compact text-ink-muted">Saved leads sync from your workspace (fixture).</div>}
          {tab === "history" && <div className="text-center py-16 text-compact text-ink-muted">Recent scans (fixture).</div>}
          {tab === "dash" && <div className="text-center py-16 text-compact text-ink-muted">Mini dashboard (fixture).</div>}
        </div>

        {/* sticky action bar 64px */}
        <div className="absolute bottom-[52px] inset-x-0 h-16 bg-surface-0 border-t border-edge-subtle flex items-center gap-2 px-3">
          <button onClick={() => toast.error("Rejected")} className="flex-1 h-10 rounded-ctrl border border-red-600/40 text-red-600 text-compact"><X className="h-4 w-4 inline mr-1" />Reject</button>
          <button onClick={() => toast("Watching")} className="flex-1 h-10 rounded-ctrl border border-amber-600/40 text-amber-600 text-compact"><Eye className="h-4 w-4 inline mr-1" />Watch</button>
          <button onClick={() => toast.success("Saved")} className="flex-1 h-10 rounded-ctrl bg-emerald-600 text-white text-compact"><Bookmark className="h-4 w-4 inline mr-1" />Save</button>
        </div>

        {/* bottom nav 52px */}
        <nav className="absolute bottom-0 inset-x-0 h-[52px] bg-navy-900 flex">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <button key={n.k} onClick={() => setTab(n.k)} data-testid={`ext-nav-${n.k}`}
                className={cn("flex-1 flex flex-col items-center justify-center gap-0.5 text-micro", tab === n.k ? "text-emerald-500" : "text-ink-inverse/60")}>
                <Icon className="h-4 w-4" />{n.label}
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
